import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Typography } from '@components/Typography';
import Button from '@components/Button';
import { AccIntegrationName } from '@constants/integrations';
import { isIntegrationConnected } from '@helpers/integrations';
import { OnboardingType } from '@constants/account';
import { googleActions } from '@actions';
import { GoogleLocationsFilters, GoogleLocationsGroups, useGoogleLocationsFilter } from '@features/GoogleLocationsList';
import GoogleCompanyItem from './GoogleCompanyItem';
import { useTranslation } from '@hooks';
import { Footer, Header, GoogleContentWrapper, GoogleFiltersWrapper, GoogleFooter, Progress } from './styles';

const ViewGoogle = ({ onboardingType, onAddManuallyClick, onConnectAnotherAccountClick, onImportCompanies }) => {
    const { t } = useTranslation();
    const [companies, setCompanies] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const account = useSelector(state => state.account?.account);
    const LOCATIONS = useSelector(state => state.google.data);
    const locationsLoading = useSelector(state => state.google.loading);
    const filter = useGoogleLocationsFilter(LOCATIONS, { withConnectedFilter: false, selected: companies });
    const dispatch = useDispatch();

    const GOOGLE_CONNECTED = isIntegrationConnected(account, AccIntegrationName.Google);

    useEffect(() => {
        if (GOOGLE_CONNECTED) {
            if (LOCATIONS.length === 0) {
                dispatch(googleActions.get());
            }
        }
    }, [dispatch, GOOGLE_CONNECTED, LOCATIONS.length]);

    const onCheckCompany = location => {
        if (companies.some(c => c.locationId === location.locationId)) {
            setCompanies(companies.filter(c => c.locationId !== location.locationId));
            return;
        }
        setCompanies([...new Set([...companies, location])]);
    };

    const selectedIds = useMemo(() => new Set(companies.map(c => c.locationId)), [companies]);
    const canDeselectAll = companies.length > 0 && filter.matchingLocations.every(l => selectedIds.has(l.locationId));

    const onCheckAll = () => {
        if (canDeselectAll) {
            setCompanies([]);
            return;
        }
        const chosenIds = new Set(selectedIds);
        const additions = filter.matchingLocations.filter(l => {
            if (chosenIds.has(l.locationId)) return false;
            chosenIds.add(l.locationId);
            return true;
        });
        setCompanies([...companies, ...additions]);
    };

    const onComplete = async () => {
        setIsSubmitting(true);
        await onImportCompanies(companies);
        setIsSubmitting(false);
    };

    return (
        <>
            <Header>
                <Typography variant="h6">{t('OnboardingPage.titleGoogle')}</Typography>
                <Typography variant="body2" sx={{ color: 'var(--text-secondary)', marginTop: '4px' }}>
                    {t('OnboardingPage.subtitleGoogle')}
                </Typography>
            </Header>
            <div style={{ gridArea: 'main', overflowY: 'auto' }}>
                {!locationsLoading ? (
                    <>
                        {filter.hasLocations && (
                            <GoogleFiltersWrapper>
                                <GoogleLocationsFilters filter={filter} />
                            </GoogleFiltersWrapper>
                        )}
                        <GoogleContentWrapper>
                            <GoogleLocationsGroups
                                filter={filter}
                                renderLocation={l => (
                                    <GoogleCompanyItem
                                        location={l}
                                        selectedCompanies={companies}
                                        setCompany={onCheckCompany}
                                    />
                                )}
                            />
                            {onboardingType !== OnboardingType.Full ? (
                                <>
                                    <Typography
                                        variant="body2"
                                        sx={{ color: 'var(--text-secondary)', marginTop: '16px' }}
                                    >
                                        {t('OnboardingPage.canTryNext')}
                                    </Typography>
                                    <GoogleFooter>
                                        <Button type="submit" variant="outlined" onClick={onConnectAnotherAccountClick}>
                                            {t('buttons.connectAnotherAccount')}
                                        </Button>
                                    </GoogleFooter>
                                </>
                            ) : (
                                <>
                                    <Typography
                                        variant="body2"
                                        sx={{ color: 'var(--text-secondary)', marginTop: '16px' }}
                                    >
                                        {t('OnboardingPage.canTryNextFull')}
                                    </Typography>
                                    <GoogleFooter>
                                        <Button type="submit" variant="outlined" onClick={onAddManuallyClick}>
                                            {t('buttons.addManually')}
                                        </Button>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                margin: '0 12px',
                                                '@media (max-width: 1024px)': {
                                                    margin: '8px 0',
                                                },
                                            }}
                                        >
                                            {t('common.or')}
                                        </Typography>
                                        <Button type="submit" variant="outlined" onClick={onConnectAnotherAccountClick}>
                                            {t('buttons.connectAnotherAccount')}
                                        </Button>
                                    </GoogleFooter>
                                </>
                            )}
                        </GoogleContentWrapper>
                    </>
                ) : (
                    <div style={{ padding: '24px' }}>
                        <Progress />
                    </div>
                )}
            </div>
            <Footer isSpaceBetween>
                <Button
                    type="submit"
                    variant="outlined"
                    onClick={onCheckAll}
                    disabled={!filter.matchingLocations.length && !companies.length}
                >
                    {canDeselectAll ? t('buttons.deselectAll') : t('buttons.selectAll')}
                </Button>
                <Button
                    type="submit"
                    variant="contained"
                    onClick={onComplete}
                    disabled={companies.length === 0 || isSubmitting}
                >
                    {t('buttons.complete')}
                </Button>
            </Footer>
        </>
    );
};

export default ViewGoogle;
